export type PlanId = 'starter' | 'professional' | 'enterprise';
export type BillingCycle = 'mensual' | 'anual';

export const MONTHLY_PRICES: Record<PlanId, number> = {
  starter: 100,
  professional: 200,
  enterprise: 500,
};

export const ANNUAL_MONTHLY_PRICES: Record<PlanId, number> = {
  starter: 80,
  professional: 160,
  enterprise: 400,
};

export const IVA_RATE = 0.15;

export interface PriceBreakdown {
  planId: PlanId;
  billingCycle: BillingCycle;
  basePrice: number;          // precio mensual base del plan (sin descuento, sin IVA)
  discountPercentage: number; // 0-100, del código promo (solo aplica en anual)
  subtotal: number;           // basePrice con descuento aplicado, sin IVA. Si es anual, ya es el monto mensual equivalente
  iva: number;                // monto de IVA sobre el subtotal
  totalMonthly: number;       // subtotal + iva (lo que se cobra/muestra mensualmente)
  totalForCharge: number;     // el monto real a cobrar en esta transacción: totalMonthly si es mensual, totalMonthly * 12 si es anual
}

export function calculatePrice(
  planId: PlanId,
  billingCycle: BillingCycle,
  discountPercentage: number = 0
): PriceBreakdown {
  const basePrice = billingCycle === 'anual' ? ANNUAL_MONTHLY_PRICES[planId] : MONTHLY_PRICES[planId];
  const discount = billingCycle === 'anual' ? discountPercentage : 0; // promo solo aplica en anual, como en el código actual
  const subtotal = Math.round(basePrice * (1 - discount / 100) * 100) / 100;
  const iva = Math.round(subtotal * IVA_RATE * 100) / 100;
  const totalMonthly = Math.round((subtotal + iva) * 100) / 100;
  const totalForCharge = billingCycle === 'anual' ? Math.round(totalMonthly * 12 * 100) / 100 : totalMonthly;

  return {
    planId,
    billingCycle,
    basePrice,
    discountPercentage: discount,
    subtotal,
    iva,
    totalMonthly,
    totalForCharge,
  };
}

export function formatUSD(amount: number): string {
  return amount.toFixed(2);
}

// ── Plan limits (bodegas, productos, usuarios, trabajadores) ───────────────────
// Fuente única de verdad: SuperAdmin.tsx (provisión) y las páginas con límites
// numéricos (Warehouses, Products, Users, WorkersPage) leen de aquí.

export type LimitResource = 'warehouses' | 'products' | 'users' | 'workers';

export const PLAN_LIMITS: Record<PlanId, Record<LimitResource, number | null>> = {
  starter:      { warehouses: 1,    products: 50,  users: 2,  workers: 10   },
  professional: { warehouses: 5,    products: 300, users: 10, workers: 50   },
  enterprise:   { warehouses: null, products: null, users: null, workers: null },
};

export const LIMIT_RESOURCE_LABELS: Record<LimitResource, string> = {
  warehouses: 'bodegas',
  products:   'productos',
  users:      'usuarios',
  workers:    'trabajadores',
};

// Empresas en trial o sin plan asignado se tratan como Starter para efectos
// de límites y mensajes de upgrade (no se les promete acceso Enterprise gratis).
export function normalizePlanForLimits(plan: string | null | undefined): PlanId {
  return plan === 'professional' || plan === 'enterprise' ? plan : 'starter';
}

export function nextPlanFor(plan: PlanId): PlanId | null {
  if (plan === 'starter') return 'professional';
  if (plan === 'professional') return 'enterprise';
  return null;
}

export function getWorkerLimit(plan: string | null | undefined): number | null {
  return PLAN_LIMITS[normalizePlanForLimits(plan)].workers;
}

// ── Plan feature gates (módulos exclusivos de un plan) ──────────────────────────
// Fuente única de verdad para los candados del sidebar (Dashboard.tsx) y los
// UpgradeWall de cada página gateada.

export interface PlanFeatureInfo {
  requiredPlan: PlanId;
  featureName:  string;
  benefits:     string[];
}

export const PLAN_FEATURES: Record<string, PlanFeatureInfo> = {
  inspecciones: {
    requiredPlan: 'enterprise',
    featureName:  'Inspecciones SST',
    benefits: [
      'Checklists de verificación según AM 196 Anexo 1',
      'Registro de hallazgos por categoría',
      '% de cumplimiento normativo automático',
    ],
  },
  iper: {
    requiredPlan: 'enterprise',
    featureName:  'Matriz de Riesgos',
    benefits: [
      'Identificación y evaluación de peligros por puesto de trabajo',
      'Matriz de riesgos según metodología GTC 45',
      'Priorización de controles y medidas preventivas',
    ],
  },
  'permisos-trabajo': {
    requiredPlan: 'enterprise',
    featureName:  'Permisos de Trabajo',
    benefits: [
      'Permisos de trabajo especiales (alturas, espacios confinados, trabajo en caliente)',
      'Checklist de verificación y flujo de cierre/cancelación',
      'Trazabilidad completa de permisos emitidos',
    ],
  },
  auditoria: {
    requiredPlan: 'professional',
    featureName:  'Historial de Cambios',
    benefits: [
      'Registro de toda la actividad de usuarios en la plataforma',
      'Trazabilidad para cumplimiento normativo',
      'Filtros por usuario, módulo y fecha, exportación a PDF',
    ],
  },
  'dashboard-sso': {
    requiredPlan: 'professional',
    featureName:  'Dashboard KPIs SSO',
    benefits: [
      'Índices de frecuencia y gravedad (IF/IG/IA)',
      'Comparación mensual de accidentalidad',
      'Visualización consolidada de cumplimiento SSO',
    ],
  },
  epp: {
    requiredPlan: 'professional',
    featureName:  'Gestión de EPP',
    benefits: [
      'Catálogo e inventario de equipo de protección personal',
      'Asignación y control de entrega a trabajadores',
      'Alertas de vencimiento y reposición',
    ],
  },
  'metodologia-iluminacion': {
    requiredPlan: 'enterprise',
    featureName:  'Metodología de Iluminación',
    benefits: [
      'Cálculo de iluminación según Art. 10 Anexo 3 AM 196',
      'Motor fotométrico real (archivos IES de fabricante)',
      'Reporte técnico descargable',
    ],
  },
  'metodologia-ruido': {
    requiredPlan: 'enterprise',
    featureName:  'Metodología de Ruido',
    benefits: [
      'Cálculo de ruido continuo e impulsivo según NTP 638 y Guía INSST 2022',
      'Comparación de métodos de atenuación (bandas de octava, HML, SNR)',
      'Reporte técnico descargable',
    ],
  },
  'metodologia-estres-termico': {
    requiredPlan: 'enterprise',
    featureName: 'Metodología de Estrés Térmico',
    benefits: [
      'Cálculo del índice WBGT según NTP 1189 (INSST) y UNE-EN ISO 7243:2017',
      'Evaluación por tasa metabólica, aclimatación y porcentaje de tiempo trabajado',
      'Reporte técnico descargable',
    ],
  },
  'metodologia-rula': {
    requiredPlan: 'enterprise',
    featureName:  'Metodología RULA',
    benefits: [
      'Evaluación de riesgo ergonómico del miembro superior según RULA (McAtamney & Corlett, 1993)',
      'Puntuación por Grupo A/B, actividad y carga/fuerza en vivo',
      'Reporte técnico descargable',
    ],
  },
  'metodologia-reba': {
    requiredPlan: 'enterprise',
    featureName:  'Metodología REBA',
    benefits: [
      'Evaluación de riesgo ergonómico de cuerpo completo según REBA (Hignett & McAtamney, 2000)',
      'Puntuación por Grupo A/B, carga-fuerza, agarre y actividad muscular en vivo',
      'Reporte técnico descargable',
    ],
  },
  'ergonomia-manipulacion-cargas': {
    requiredPlan: 'enterprise',
    featureName: 'Ecuación de NIOSH',
    benefits: [
      'Evaluación de riesgo por manejo manual de cargas según NIOSH (Waters, Putz-Anderson & Garg, 1994)',
      'Peso Máximo Recomendado (RWL) e Índice de Levantamiento por tarea, con soporte multitarea (Índice Compuesto)',
      'Reporte técnico descargable',
    ],
  },
  'metodologia-snook-ciriello': {
    requiredPlan: 'enterprise',
    featureName: 'Tablas de Snook y Ciriello',
    benefits: [
      'Evaluación de transporte, empuje y arrastre de cargas según Snook y Ciriello (1991), percentil 90 — fuente INSST',
      'Peso o fuerza máxima aceptable comparado contra el valor real de la tarea',
      'Reporte técnico descargable',
    ],
  },
  'ergonomia-puestos-oficina': {
    requiredPlan: 'enterprise',
    featureName: 'Metodología ROSA',
    benefits: [
      'Evaluación ergonómica de puestos de oficina según ROSA (Sonne, Villalta & Andrews, 2012)',
      'Puntuación de silla, pantalla/teléfono y ratón/teclado, con nivel de actuación en vivo',
      'Reporte técnico descargable',
    ],
  },
};

const PLAN_RANK: Record<string, number> = {
  trial: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
};

export function hasPlanAccess(currentPlan: string | null | undefined, moduleId: string): boolean {
  const required = PLAN_FEATURES[moduleId];
  if (!required) return true;
  if (!currentPlan) return false;
  return (PLAN_RANK[currentPlan] ?? 0) >= PLAN_RANK[required.requiredPlan];
}
